import { AlertCircle, AlertTriangle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

/**
 * BE-locked link-state union per BE TRD §"Existing roster row props
 * gain". Active state ships as `null` (BE keeps the roster quiet —
 * negative-asserted in `tiktok-link-state-cue.test.tsx`).
 */
export type TiktokLinkState = "refresh_needed" | "revoked"

export interface TiktokLinkStateCueProps {
  /** Server-shipped flat field on roster row props
   *  (`tiktok_link_state: "refresh_needed" | "revoked" | null`). The
   *  component renders nothing when the value is `null` or undefined. */
  state: TiktokLinkState | null | undefined
  className?: string
}

/**
 * TiktokLinkStateCue — small inline pill on candidate roster rows
 * (brand/agency view) when a creator's TikTok link needs attention.
 *
 * Per UX Spec §1.E + §6:
 *   - **Active state ⇒ NO badge rendered** (BE ships `null`; FE
 *     defensive when consumer passes `"active"` strings — guarded).
 *     Negative-asserted in Vitest: `state={null}` → component returns
 *     `null`.
 *   - `refresh_needed` ⇒ amber badge "Reconnect needed" with TikTok
 *     glyph icon, testid `tiktok-link-health-refresh`.
 *   - `revoked` ⇒ red badge "Disconnected" with stronger icon, testid
 *     `tiktok-link-health-revoked`.
 *
 * On mobile (≤640px) the badge truncates to icon-only with the full
 * label preserved as `aria-label` (UX Spec §6). The Tooltip surface
 * keeps the affordance discoverable on tap.
 *
 * a11y:
 *   - Color is never the sole indicator — every state pairs an icon +
 *     text label.
 *   - `aria-label` carries the full state text even when visually
 *     truncated.
 *   - Tooltip is keyboard-accessible (Radix focus trap).
 */
export function TiktokLinkStateCue({
  state,
  className = "",
}: TiktokLinkStateCueProps) {
  const { t } = useTranslation()
  if (state !== "refresh_needed" && state !== "revoked") return null

  const isRevoked = state === "revoked"
  const variant = isRevoked ? "destructive" : "secondary"
  const Icon = isRevoked ? AlertCircle : AlertTriangle
  const label = isRevoked
    ? t("tiktok.link_state.revoked")
    : t("tiktok.link_state.refresh_needed")
  const tooltip = isRevoked
    ? t("tiktok.link_state.revoked_tooltip")
    : t("tiktok.link_state.refresh_needed_tooltip")
  const testId = isRevoked
    ? "tiktok-link-health-revoked"
    : "tiktok-link-health-refresh"

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger
          render={
            <Badge
              variant={variant}
              data-testid={testId}
              data-state={state}
              aria-label={label}
              className={[
                "inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium",
                isRevoked
                  ? // Refresh-needed amber tint via Tailwind utility — shadcn's
                    // `secondary` variant is too neutral; we approximate the
                    // amber the UX Spec calls for. Revoked uses `destructive`
                    // verbatim.
                    ""
                  : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100",
                className,
              ].join(" ")}
            >
              <Icon className="size-3" aria-hidden="true" />
              {/* Mobile: hide the text via sm:inline; aria-label
                preserves the announcement so SR users still get the
                full state. */}
              <span className="hidden sm:inline">{label}</span>
            </Badge>
          }
        />
        <TooltipContent data-testid={`${testId}-tooltip`} side="top">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default TiktokLinkStateCue
